import find from 'lodash.find'

import NumberUtils from './NumberUtils'

const defaults = {
  SCROLL_THRESHOLD: 0.9
}

const settings = {
  SHOW_ONCE_ATTR: 'data-scroll-item-once',
  SHOW_PROGRESS_ATTR: 'data-scroll-item-progress',
  SHOW_CLASS_NAME: 'show-from-scroll',
  SCROLL_SHOW_TIMEOUT: 150
}

function getElHeight(el) {
  const height = el.offsetHeight
  const { marginTop, marginBottom } = window.getComputedStyle(el)

  return height + parseInt(marginTop, 10) + parseInt(marginBottom, 10)
}

function getElOffset(el) {
  const pageScrollPos = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
  return Math.floor(el.getBoundingClientRect().top + pageScrollPos)
}

class ScrollItemManager {

  setup(config) {
    this.config = config
    this.ticking = false
    this.lastScrollY = window.pageYOffset

    if (!this.canSupport()) {
      document.documentElement.className += ' no-scroll-transitions'
      return
    }

    this._bindClassMethods()

    this.getItems()
    this.cacheWindowDimensions()
    this.cacheItemPositions()
    this.bindEvents()

    this.checkItemOncePositions(false)
    this.checkItemProgressPositions()
  }

  _bindClassMethods() {
    this.onResize = this.onResize.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.scrollUpdate = this.scrollUpdate.bind(this)
  }

  canSupport() {
    let s = document.createElement('p').style
    let supports = 'transition' in s ||
      'WebkitTransition' in s ||
      'MozTransition' in s ||
      'msTransition' in s ||
      'OTransition' in s
    return supports
  }

  getItems() {
    this._itemsOnce = Array.prototype.slice
      .call(document.body.querySelectorAll(`[${settings.SHOW_ONCE_ATTR}]`))
      .map(el => {
        const type = el.getAttribute(`${settings.SHOW_ONCE_ATTR}`)
        const config = find(this.config, { type })
        return {
          el,
          type,
          config,
          shown: false
        }
      })

    this._itemsProgress = Array.prototype.slice
      .call(document.body.querySelectorAll(`[${settings.SHOW_PROGRESS_ATTR}]`))
      .map(el => {
        const type = el.getAttribute(`${settings.SHOW_PROGRESS_ATTR}`)
        const config = find(this.config, { type })
        return {
          el,
          type,
          config
        }
      })
  }

  cacheWindowDimensions() {
    this.windowDims = {
      width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
    }
  }

  cacheItemPositions() {
    this._itemsOnce.forEach(item => this._cacheItemPosition(item))
    this._itemsProgress.map(item => this._cacheItemPosition(item))
  }

  _cacheItemPosition(item) {
    item.dims = {
      height: getElHeight(item.el),
      offset: getElOffset(item.el)
    }
    return item
  }

  bindEvents() {
    window.addEventListener('resize', this.onResize)
    window.addEventListener('scroll', this.onScroll)
  }

  onResize() {
    this.cacheWindowDimensions()
    this.cacheItemPositions()
  }

  onScroll(e) {
    this.lastScrollY = window.pageYOffset
    this.requestTick()
  }

  requestTick() {
    if (!this.ticking) {
      window.requestAnimationFrame(this.scrollUpdate)
      this.ticking = true
    }
  }

  scrollUpdate() {
    this.ticking = false

    clearTimeout(this.timerScroll)

    this.timerScroll = setTimeout(() => {
      this.cacheItemPositions()
    }, 50)

    this.checkItemOncePositions(true)
    this.checkItemProgressPositions()
  }

  checkItemOncePositions(fromScroll = false) {
    const itemsToShow = this._itemsOnce
      .filter(item => !item.shown)
      .filter(item => {
        const threshold = (item.config && item.config.thresholdStart) ? item.config.thresholdStart : defaults.SCROLL_THRESHOLD
        const thresholdValue = this.lastScrollY + (this.windowDims.height * threshold)
        if (thresholdValue > item.dims.offset) {
          item.shouldDelay = !(!fromScroll && thresholdValue > (item.dims.offset + item.dims.height))
          return item;
        }
        return null;
      })

    if (itemsToShow.length) this.showItems(itemsToShow)
  }

  checkItemProgressPositions() {
    this._itemsProgress.forEach(item => this.setItemProgress(item))
  }

  showItems(items) {
    let delayCount = 0

    items.forEach((item, i) => {
      ((_item) => {
        let delay = 0

        if (_item.shouldDelay) {
          delay = settings.SCROLL_SHOW_TIMEOUT * delayCount
          delayCount++
        }

        setTimeout(() => {
          this.showItem(_item)
        }, delay)
      })(item, i)
    })
  }

  showItem(item) {
    const className = (item.config && item.config.classToAdd) ? item.config.classToAdd : settings.SHOW_CLASS_NAME

    if (item.el.classList.contains(className)) return

    item.el.classList.add(className)
    item.shown = true
  }

  setItemProgress(item) {
    const min = item.dims.offset + (this.windowDims.height * item.config.thresholdStart)
    const max = item.dims.offset + item.dims.height + (this.windowDims.height * item.config.thresholdEnd)
    const progress = NumberUtils.map(this.lastScrollY + this.windowDims.height, min, max, 0, 1)

    item.config.callback(item, progress)
  }

}

export default ScrollItemManager
export { settings }
